import type { AnalyteKind } from "./catalogue.js";
import type { ResultColour, ResultFlag } from "./work.js";

/**
 * Reference-interval selection, mechanical flags and the staff colour code.
 *
 * Flags and colours talk to the chemist, never to the patient (NOM-007 5.1.12, research/03):
 * the report prints the flag letter and the interval; it never prints a colour or a sentence.
 */

/** One interval row as frozen inside the order item's snapshot. */
export interface SnapshotInterval {
  id: string;
  sex: "M" | "F" | null;
  age_min_days: number | null;
  age_max_days: number | null;
  condition: string | null;
  low: number | null;
  high: number | null;
  expected_text: string | null;
  display_text: string | null;
  /** The branch whose own set this row came from; null or absent = a general row (ADR 0020). */
  branch_id?: string | null;
}

/**
 * The rows an order at `branchId` chooses from (ADR 0020): the analyte's rows for that branch when
 * it has any, else its general rows. Another branch's rows never apply. Sex, age and condition then
 * pick within the set (`pickInterval`).
 */
export function intervalsForBranch<T extends { branch_id?: string | null }>(rows: readonly T[], branchId: string | null): T[] {
  const own = branchId === null ? [] : rows.filter((r) => r.branch_id === branchId);
  return own.length > 0 ? own : rows.filter((r) => (r.branch_id ?? null) === null);
}

export interface SnapshotAnalyte {
  id: string;
  code: string;
  name: string;
  kind: AnalyteKind;
  unit: string | null;
  decimals: number | null;
  options: string[] | null;
  formula: string | null;
  section: string | null;
  sort_order: number;
  /** The row applied for this patient (sex, age, condition), or null when none matched. */
  interval: SnapshotInterval | null;
  /** Text printed in the "Referencia" column: the applied row, or every row when none matched. */
  interval_text: string | null;
}

export interface StudySnapshot {
  snapshot_version: 1;
  study: {
    id: string;
    clave: string;
    name: string;
    version: number;
    kind: "analytic" | "service";
    area_id: string | null;
    area_name: string | null;
    area_sort_order: number;
    sample_type_id: string | null;
    sample_type_name: string | null;
    container_id: string | null;
    container_name: string | null;
    container_colour_hex: string | null;
    container_colour_name: string | null;
    /** Effective values at the order's branch: its override (migration 0015) else the study's. */
    method_name: string | null;
    technique_name: string | null;
    equipment_name: string | null;
    /** The branch the method/technique/equipment were resolved for; absent on snapshots taken
     *  before per-branch overrides existed (and on imported history). */
    resolved_branch_id?: string | null;
    indications: string | null;
    delivery_days: number | null;
    /** Weekday table (ADR 0027); absent on snapshots taken before migration 0028. */
    delivery_by_weekday?: number[] | null;
  };
  patient: { sex: "M" | "F"; age_days: number; condition: string | null };
  analytes: SnapshotAnalyte[];
}

/**
 * Pick the interval row for a patient. Specific beats generic: a row matching the order's
 * condition beats one without; then sex-specific beats any-sex; then an age-bounded row beats
 * an open one. Rows that contradict the patient (other sex, age outside) never match.
 */
export function pickInterval<T extends SnapshotInterval>(rows: T[], p: { sex: "M" | "F"; age_days: number; condition: string | null }): T | null {
  const cond = p.condition?.trim().toLowerCase() || null;
  let best: T | null = null;
  let bestScore = -1;
  for (const r of rows) {
    if (r.sex !== null && r.sex !== p.sex) continue;
    if (r.age_min_days !== null && p.age_days < r.age_min_days) continue;
    if (r.age_max_days !== null && p.age_days > r.age_max_days) continue;
    const rc = r.condition?.trim().toLowerCase() || null;
    if (rc !== null && rc !== cond) continue;
    let score = 0;
    if (rc !== null) score += 100;
    if (r.sex !== null) score += 10;
    if (r.age_min_days !== null || r.age_max_days !== null) score += 1;
    if (score > bestScore) {
      best = r;
      bestScore = score;
    }
  }
  return best;
}

export function intervalDisplay(r: SnapshotInterval, unit: string | null): string | null {
  if (r.display_text) return r.display_text;
  const u = unit ? ` ${unit}` : "";
  if (r.low !== null && r.high !== null) return `${fmtNum(r.low)} - ${fmtNum(r.high)}${u}`;
  if (r.high !== null) return `Menor de ${fmtNum(r.high)}${u}`;
  if (r.low !== null) return `Mayor de ${fmtNum(r.low)}${u}`;
  if (r.expected_text) return r.expected_text;
  return null;
}

/**
 * The "Referencia" text when no row applies to the patient: every row's text, one per line, each
 * distinct text once (the rows structured from one laboratory text all print that same text).
 */
export function allIntervalsText(rows: readonly SnapshotInterval[], unit: string | null): string | null {
  const out: string[] = [];
  for (const r of rows) {
    const t = intervalDisplay(r, unit);
    if (t && !out.includes(t)) out.push(t);
  }
  return out.length ? out.join("\n") : null;
}

function fmtNum(n: number): string {
  return Number.isInteger(n) ? String(n) : String(n);
}

// ---- narrowing an imported block to the population that applies (R-47) ----
// The previous system stored one reference TEXT per analyte and repeated it on every structured
// row, so a laboratory migrated from it has the whole block ("Hombres: … / Mujeres: … / Niños: …")
// as the `display_text` of each row, including the one that applies. The flag is already computed
// against the row's own bounds (`pickInterval` + `computeFlag`), so printing the three lines only
// invites reading the wrong one. Very, 2026-11-04: «Nos gustaría que de acuerdo al sexo o la edad
// pudiera marcarlos el sistema por sí solo» → it already does; she was offered the printed column
// narrowed to her patient's line and answered «Sí por favor» (Fernanda, in the group, 17:32).
//
// The narrowing is presentation only: the snapshot keeps the laboratory's text whole (ADR 0009),
// so an old report still renders narrowed and nothing stored is rewritten.

const SEX_WORDS: Record<"M" | "F", RegExp> = { M: /\b(hombre|masculin|var[oó]n)/i, F: /\b(mujer|femenin)/i };
const OTHER_SEX: Record<"M" | "F", RegExp> = { M: SEX_WORDS.F, F: SEX_WORDS.M };

/** Every number a reference line names, as numbers ("Hombres:0.70 a 1.40" → [0.7, 1.4]). */
function numbersIn(line: string): number[] {
  return (line.match(/-?\d+(?:[.,]\d+)?/g) ?? []).map((n) => Number(n.replace(",", ".")));
}

function sameNumber(a: number, b: number): boolean {
  return Math.abs(a - b) < 1e-9;
}

/**
 * The reference text to show for the row that applied, narrowed to that row's own line when the
 * text is a block of several populations. A line is the applied row's when it names every bound
 * the row has (0.70 and 1.40), or, failing that, when it is the only one naming the row's sex.
 * When no single line can be told apart the whole text is kept: showing all the populations is
 * confusing, showing the wrong one is an error on a clinical document.
 */
export function appliedIntervalText(text: string | null, applied: SnapshotInterval | null): string | null {
  if (!text || !applied) return text;
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return text;

  const bounds = [applied.low, applied.high].filter((b): b is number => b !== null);
  if (bounds.length > 0) {
    const byBounds = lines.filter((l) => {
      const ns = numbersIn(l);
      return bounds.every((b) => ns.some((n) => sameNumber(n, b)));
    });
    if (byBounds.length === 1) return byBounds[0]!;
  }

  if (applied.sex) {
    const bySex = lines.filter((l) => SEX_WORDS[applied.sex as "M" | "F"].test(l) && !OTHER_SEX[applied.sex as "M" | "F"].test(l));
    if (bySex.length === 1) return bySex[0]!;
  }

  return text;
}

/** A typed text that is a plain number ("1.20", "1,20", "-3"), as a number; anything else is null. */
export function plainNumber(text: string | null): number | null {
  if (text === null) return null;
  const t = text.trim();
  return /^[+-]?\d+(?:[.,]\d+)?$/.test(t) ? Number(t.replace(",", ".")) : null;
}

/**
 * Mechanical flag against the applied interval. Null when there is nothing to compare.
 *
 * A text analyte whose applied interval has bounds is judged like a numeric one when the typed
 * value is a plain number (ADR 0020): the previous system typed most quantitative analytes as
 * text (CREATININA, TGO…), and the capture preview has judged them that way since it was written.
 * Any other text ("<0.5", "NO SE OBSERVA") is not compared with the bounds.
 */
export function computeFlag(
  kind: AnalyteKind,
  value: { numeric: number | null; text: string | null },
  interval: SnapshotInterval | null,
): ResultFlag | null {
  if (kind === "subtitle" || kind === "document") return null;
  if (!interval) return null;
  const bounded = interval.low !== null || interval.high !== null;
  const n = kind === "numeric" ? value.numeric : bounded ? plainNumber(value.text) : null;
  if (kind === "numeric" || n !== null) {
    if (n === null) return null;
    if (interval.low !== null && n < interval.low) return "L";
    if (interval.high !== null && n > interval.high) return "H";
    if (!bounded) return null;
    return "N";
  }
  // text / reference
  if (value.text === null || !interval.expected_text) return null;
  return norm(value.text) === norm(interval.expected_text) ? "N" : "A";
}

function norm(s: string): string {
  return s.trim().toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
}

/**
 * Staff colour: green normal, yellow (drawn orange-brown) outside the interval, high or low. The
 * catalogue has no per-analyte critical limits, so nothing is ever "red" here: an earlier distance
 * rule ("more than a quarter of the interval width beyond the bound") marked values such as
 * glucose 520 or urea 1 as critical with no clinical basis, and is gone (Very, 2026-11-03).
 * Red returns only when real critical limits exist in the catalogue. Text mismatches are
 * orange too. This is a screen aid only.
 */
export function computeColour(flag: ResultFlag | null, _numeric: number | null, _interval: SnapshotInterval | null): ResultColour {
  if (flag === null) return "none";
  return flag === "N" ? "green" : "yellow";
}

/** The bounds a printed number is read against: the applied interval's `low` / `high`. */
export interface PrintedBounds {
  low: number | null;
  high: number | null;
}

/** How many decimals a number carries as stored (31.7 → 1, 32 → 0, 7.88 → 2); exponent forms count as 6. */
function ownDecimals(n: number): number {
  const s = String(n);
  if (/e/i.test(s)) return 6;
  const dot = s.indexOf(".");
  return dot === -1 ? 0 : s.length - dot - 1;
}

/**
 * The decimals a number prints with so that it never contradicts its own flag (ADR 0039, pilot
 * R-55, 2026-11-05). The catalogue's `decimals` is the laboratory's choice of precision; but a
 * value rounded to it can land inside the printed interval — or exactly on a limit — while the
 * flag, computed from the stored value, says it is outside (CMHC 31.7 → "32", flagged *B against
 * 32 - 37). A chemist reads that as a bug in the flags; a patient reads a normal result marked
 * abnormal. So the rule: the printed number must stand in the same relation to the limits as the
 * flag does — below `low` for L, above `high` for H, within for N. When the catalogue's decimals
 * do not achieve that, the fewest extra decimals that do are used, never more than the value
 * itself carries (at its own precision the relation holds by construction, since the flag was
 * computed from it). Returns the decimals to print with; `null` means "as stored" (no catalogue
 * decimals).
 *
 * Without a flag, without bounds, or for a text flag (A), the catalogue's decimals stand: there
 * is nothing on the sheet to contradict.
 */
export function printedDecimals(numeric: number, decimals: number | null, flag: ResultFlag | null, bounds: PrintedBounds | null): number | null {
  if (decimals === null) return null;
  if (!flag || flag === "A" || !bounds) return decimals;
  const { low, high } = bounds;
  if (low === null && high === null) return decimals;
  const consistent = (p: number): boolean => {
    if (flag === "L") return low !== null && p < low;
    if (flag === "H") return high !== null && p > high;
    return (low === null || p >= low) && (high === null || p <= high);
  };
  const most = Math.max(decimals, ownDecimals(numeric));
  for (let k = decimals; k <= most; k++) {
    if (consistent(Number(numeric.toFixed(k)))) return k;
  }
  return most;
}

/**
 * Value as it prints: fixed decimals for numerics, the text otherwise. With `judged` (the row's
 * flag and the applied interval's bounds) the decimals are `printedDecimals`: the report and
 * every screen that shows a value next to its flag go through here, so the sheet and the
 * screen never disagree.
 */
export function formatValue(kind: AnalyteKind, numeric: number | null, text: string | null, decimals: number | null, judged?: { flag: ResultFlag | null; bounds: PrintedBounds | null }): string {
  if (kind === "numeric" && numeric !== null) {
    const d = judged ? printedDecimals(numeric, decimals, judged.flag, judged.bounds) : decimals;
    return d === null ? String(numeric) : numeric.toFixed(d);
  }
  return text ?? "";
}

/** Analytes that must carry a value before an item counts as captured. */
export function needsValue(kind: AnalyteKind): boolean {
  return kind === "numeric" || kind === "text" || kind === "reference" || kind === "document";
}

// -- Age units -----------------------------------------------------------------------------------

export type AgeBandUnit = "d" | "m" | "a";

/** Mean Gregorian year. A flat 365 drifts four days by 16 years and hands a 15-year-old adult ranges. */
export const DAYS_PER_YEAR = 365.2425;

/**
 * An age in days for a value in `unit` (días, meses, años). A year is the calendar mean, rounded
 * to whole days (15 años = 5479); a month stays 30 days, the convention every stored row and the
 * importer already use. Patient ages are whole elapsed days (`ageDays` in the API), so a band
 * written in years lines up with birthdays to within a day.
 */
export function ageToDays(value: number, unit: AgeBandUnit): number {
  if (unit === "a") return Math.round(value * DAYS_PER_YEAR);
  return Math.round(value * (unit === "m" ? 30 : 1));
}

/** The inverse of `ageToDays`: the value of `days` in `unit`, or null when it is not an exact multiple. */
export function daysToAgeValue(days: number, unit: AgeBandUnit): number | null {
  if (unit === "d") return days;
  if (unit === "m") return days % 30 === 0 ? days / 30 : null;
  const k = Math.round(days / DAYS_PER_YEAR);
  if (k > 0 && ageToDays(k, "a") === days) return k;
  // Rows saved before the calendar year existed are multiples of 365; they still read as years.
  return days % 365 === 0 ? days / 365 : null;
}
